import { useCallback, useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { getUserAccess, getUserL1Access } from 'Deer/helpers/getUserAccess';
import { useDeerViewId } from 'Deer/hooks/useDeerViewId';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useQuery } from 'components/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import { TABLE_TYPES_EVENT_VALUE_NAME } from 'screens/performanceReview/LandingPage/ReviewAuditIntent';
import {
  REVIEWS_CALIBRATE_PAGE_DEFAULT_COLUMN_ORDER,
  REVIEWS_MANAGE_PAGE_DEFAULT_COLUMN_ORDER,
  REVIEW_MANAGE_GRID_COLUMN_ID,
} from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageAGGrid/reviewMangeGridConstants';
import {
  checkActionPlanFeatureEnabled,
  checkDemoAICalibrationEnabled,
  checkReviewReportsEnabled,
  checkSimplifiedReviewCreationEnabled,
  checkVisierAnalyticsEnabled,
} from 'utils/helpers/featureFlags';
import { checkReviewModuleEnabled } from 'utils/helpers/moduleAccess';
import { checkUserIsCustomReviewAdmin, checkUserIsReviewAdmin } from 'utils/helpers/rolesAccess';

// import { checkUserIsReviewAdmin } from 'utils/helpers/rolesAccess';
import { isMobileView } from 'constants/isMobileView';
import { sendViewReviewListEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';
import { isNetchexLogin } from 'constants/netChexConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { useReviewsIntentEllipsisValues } from '../hooks/useReviewsIntentElllipsisValues';
import { ReviewsIntentType, setSelectedReviewsIntent } from '../slice/ReviewsIntentSlice';
import { MetaFilters } from './MetaFilters';

export const REVIEW_TABLE_TYPES = {
  PENDING_ON_ME: 'PENDING_ON_ME',
  PENDING_ON_OTHERS: 'PENDING_ON_OTHERS',
  COMPLETED_BY_ME: 'COMPLETED_BY_ME',
  MY_PAST_REVIEWS: 'MY_PAST_REVIEWS',
};

const getRouteHelper = ({ activeTableId, selectedReviewCycleId }) =>
  `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${activeTableId}&reviewCycleId=${selectedReviewCycleId}`;

const useSelectedTableAndReviewCycleId = () => {
  const { selectedReviewCycle } = useReviewManageSidepanelData();

  const reduxStoreReviewCycleId = selectedReviewCycle?.uuid;

  const { activeTableId = '', reviewCycleId: queryParamReviewCycleId = '' } = useQuery();
  const selectedTableId = activeTableId !== '' ? activeTableId : null;

  let selectedReviewCycleId;
  if (queryParamReviewCycleId === '') {
    selectedReviewCycleId = queryParamReviewCycleId;
  } else if (queryParamReviewCycleId) {
    selectedReviewCycleId = ['null', ''].includes(queryParamReviewCycleId) ? null : Number(queryParamReviewCycleId);
  } else if (reduxStoreReviewCycleId) {
    selectedReviewCycleId = reduxStoreReviewCycleId;
  } else {
    selectedReviewCycleId = null;
  }
  return { selectedTableId, selectedReviewCycleId };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IntentDropdown = ({
  gridRef,
  showKebabMenu = true,
  breadcrumbs,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef?: any;
  showKebabMenu?: boolean;
  breadcrumbs: IntentBreadcrumbType;
}) => {
  const history = useHistory();
  const { isSkipManager, isDirectManager, isCustomReviewAdmin } = getUserAccess();
  const { hasL1CompanyAccess, hasViewsAccess } = getUserL1Access();
  const { viewId: viewIdFromQueryParams } = useDeerViewId();

  const userIsManager = isSkipManager || isDirectManager;

  const { pathname } = useLocation();
  const { l1Module } = useRouteToNavLevels();
  const dispatch = useDispatch();
  const { teamType } = useRouteToMyTeamLevels();

  const queryClient = useQueryClient();

  const { selectedReviewCycleId, selectedTableId } = useSelectedTableAndReviewCycleId();

  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
  const isDetailedReportPage = history.location.pathname.includes(routes.performanceReview.detailedReports);
  const isActionPlanPage = !!matchPath(pathname, {
    path: L3_PATHS.team.directReports.review.actionPlan,
    exact: true,
  });

  const reviewLandingPageIntentChangeHandler = useCallback(
    (newSelectedTableId) => {
      sendViewReviewListEvent({
        eventProperties: {
          type: TABLE_TYPES_EVENT_VALUE_NAME[newSelectedTableId],
        },
      });
      history.push(
        getRouteHelper({
          activeTableId: newSelectedTableId,
          selectedReviewCycleId,
        })
      );
      // to update the table to get latest data
      queryClient.invalidateQueries([REACT_QUERY_KEYS.ReviewSections]);
    },
    [history, queryClient, selectedReviewCycleId]
  );

  const ellipsisMenuOptions = useReviewsIntentEllipsisValues({
    gridRef,
  });

  const reviewCalibratedValues = useCallback(() => {
    const val = {};
    // specially adding talentPlanning route key.
    // because we show same intent for two different type of pages, Calibration and calibration view
    val.calibrationViewValues = {};
    val.calibrationViewValues.team = {};

    if (isSkipManager || isDirectManager || hasViewsAccess) {
      val.team = {};
      if (isDirectManager) {
        val.team.directReports = {
          path: L3_PATHS.team.directReports.review.calibratedResults,
          isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
        };
      }
      if (isSkipManager) {
        val.team.allReports = {
          path: L3_PATHS.team.allReports.review.calibratedResults,
          isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
        };
      }

      val.team.view = {
        path: L3_PATHS.team.view.review.calibratedResults,
        isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
      };

      // special addition
      val.calibrationViewValues.team.view = {
        path: L3_PATHS.team.view.review.calibrationView,
        isAccessible: checkReviewModuleEnabled,
      };
    }

    if (hasL1CompanyAccess) {
      val.company = {
        path: L3_PATHS.company.review.calibratedResults,
        isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
      };

      val.calibrationViewValues.company = {
        path: L3_PATHS.company.review.calibrationView,
        isAccessible: () => checkUserIsReviewAdmin() || isCustomReviewAdmin,
      };
    }
    return val;
  }, [hasL1CompanyAccess, hasViewsAccess, isCustomReviewAdmin, isDirectManager, isSkipManager, userIsManager]);

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<ReviewsIntentType>>(() => {
    let options = [];
    options.push({
      id: REVIEW_INTENTS.PENDING_ON_ME,
      label: `Pending on me`,
      onClick: () => {
        reviewLandingPageIntentChangeHandler(REVIEW_TABLE_TYPES.PENDING_ON_ME);
      },
      visible: () => l1Module === 'myData',
      value: {
        myData: {
          path: L3_PATHS['my-data'].review.myReviews,
          isAccessible: checkReviewModuleEnabled,
        },
      },
      queryParamValue: REVIEW_TABLE_TYPES.PENDING_ON_ME,
      category: 'Manage',
      hasMetaFilters: true,
    });

    // if it's mobile view show only pending on me option
    if (isMobileView() && !isNetchexLogin) {
      return options;
    }

    options = [
      ...options,
      {
        id: REVIEW_INTENTS.PENDING_ON_OTHERS,
        label: `Pending on others`,
        onClick: () => {
          reviewLandingPageIntentChangeHandler(REVIEW_TABLE_TYPES.PENDING_ON_OTHERS);
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].review.myReviews,
            isAccessible: checkReviewModuleEnabled,
          },
        },
        queryParamValue: REVIEW_TABLE_TYPES.PENDING_ON_OTHERS,
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: REVIEW_INTENTS.COMPLETED_BY_ME,
        label: `Completed by me`,
        onClick: () => {
          reviewLandingPageIntentChangeHandler(REVIEW_TABLE_TYPES.COMPLETED_BY_ME);
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].review.myReviews,
            isAccessible: checkReviewModuleEnabled,
          },
        },
        queryParamValue: REVIEW_TABLE_TYPES.COMPLETED_BY_ME,
        category: 'Manage',
        hasMetaFilters: true,
      },
      {
        id: REVIEW_INTENTS.MY_PAST_REVIEWS,
        label: `Past reviews`,
        onClick: () => {
          reviewLandingPageIntentChangeHandler(REVIEW_TABLE_TYPES.MY_PAST_REVIEWS);
        },
        visible: () => l1Module === 'myData',
        value: {
          myData: {
            path: L3_PATHS['my-data'].review.myReviews,
            isAccessible: checkReviewModuleEnabled,
          },
        },
        queryParamValue: REVIEW_TABLE_TYPES.MY_PAST_REVIEWS,
        category: 'Manage',
        hasMetaFilters: false,
      },
      {
        id: REVIEW_INTENTS.REVIEW_COMPLETION_STATUS,
        label: `Completion status`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.review.completionStatus,
              allReports: L3_PATHS.team.allReports.review.completionStatus,
              view: viewIdFromQueryParams
                ? `${L3_PATHS.team.view.review.completionStatus}?viewId=${viewIdFromQueryParams}`
                : L3_PATHS.team.view.review.completionStatus,
            },
            company: L3_PATHS.company.review.completionStatus,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () => l1Module !== 'myData',
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.review.completionStatus,
              isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
            },
            allReports: {
              path: L3_PATHS.team.allReports.review.completionStatus,
              isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
            },
            view: {
              path: L3_PATHS.team.view.review.completionStatus,
              isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
            },
          },
          company: {
            path: L3_PATHS.company.review.completionStatus,
            isAccessible: () => userIsManager || checkUserIsReviewAdmin(),
          },
        },
        category: 'Manage',
        tableColumnsToSelect: [
          REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE,
          REVIEW_MANAGE_GRID_COLUMN_ID.RELATIONSHIP,
          REVIEW_MANAGE_GRID_COLUMN_ID.REVIEW_STATUS,
          REVIEW_MANAGE_GRID_COLUMN_ID.SELF_REVIEW_STATUS,
          REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER,
          REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER_REVIEW_STATUS,
        ],
        tableColumnsLockVisible: [REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE],
        columnDefaultOrder: REVIEWS_MANAGE_PAGE_DEFAULT_COLUMN_ORDER,
        hasMetaFilters: true,
      },
      {
        id: REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS,
        label: l1Module === 'team' && teamType === 'directReports' ? `Calibrated reviews` : `Calibration`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.review.calibratedResults,
              allReports: L3_PATHS.team.allReports.review.calibratedResults,
              view: viewIdFromQueryParams
                ? `${L3_PATHS.team.view.review.calibratedResults}?viewId=${viewIdFromQueryParams}`
                : L3_PATHS.team.view.review.calibratedResults,
            },
            company: L3_PATHS.company.review.calibratedResults,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () => l1Module !== 'myData',
        value: reviewCalibratedValues(),
        category: 'Manage',
        tableColumnsToSelect: [
          REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE,
          REVIEW_MANAGE_GRID_COLUMN_ID.RELATIONSHIP,
          ...(checkDemoAICalibrationEnabled() ? [REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION] : []),
          REVIEW_MANAGE_GRID_COLUMN_ID.REVIEW_STATUS,
          REVIEW_MANAGE_GRID_COLUMN_ID.CALIBRATION_STATUS,
          REVIEW_MANAGE_GRID_COLUMN_ID.OVERALL_RATING,
          REVIEW_MANAGE_GRID_COLUMN_ID.OUTCOME,
          REVIEW_MANAGE_GRID_COLUMN_ID.GRID_POSITION,
          REVIEW_MANAGE_GRID_COLUMN_ID.LAST_COMMENT,
          REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER,
          REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER_OVERALL_RATING,
          REVIEW_MANAGE_GRID_COLUMN_ID.MANAGER_OUTCOME,
          REVIEW_MANAGE_GRID_COLUMN_ID.SELF_OVERALL_RATING,
        ],
        tableColumnsLockVisible: [
          REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE,
          ...(checkDemoAICalibrationEnabled() ? [REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION] : []),
        ],
        columnDefaultOrder: REVIEWS_CALIBRATE_PAGE_DEFAULT_COLUMN_ORDER,
        hasMetaFilters: true,
      },
      {
        label: `Reports`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.review.embeddedAnalytics,
              allReports: L3_PATHS.team.allReports.review.embeddedAnalytics,
            },
            company: L3_PATHS.company.review.embeddedAnalytics,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () =>
          checkVisierAnalyticsEnabled() &&
          (l1Module === 'company' || (l1Module === 'team' && teamType !== 'view')) &&
          checkUserIsReviewAdmin(),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.review.embeddedAnalytics,
              isAccessible: () => checkVisierAnalyticsEnabled(),
            },
            allReports: {
              path: L3_PATHS.team.allReports.review.embeddedAnalytics,
              isAccessible: () => checkVisierAnalyticsEnabled(),
            },
          },
          company: {
            path: L3_PATHS.company.review.embeddedAnalytics,
            isAccessible: () => checkVisierAnalyticsEnabled(),
          },
        },
        category: 'Analyze',
        hasMetaFilters: false,
      },
      {
        id: REVIEW_INTENTS.REVIEW_DETAILED_REPORTS,
        label: `Data exports`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.review.detailReports,
              allReports: L3_PATHS.team.allReports.review.detailReports,
              view: viewIdFromQueryParams
                ? `${L3_PATHS.team.view.review.detailReports}?viewId=${viewIdFromQueryParams}`
                : L3_PATHS.team.view.review.detailReports,
            },
            company: L3_PATHS.company.review.detailReports,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.team.allReports);
          history.push(urlToPush);
        },
        visible: () =>
          checkReviewReportsEnabled() &&
          (l1Module === 'company' || l1Module === 'team') &&
          (checkUserIsReviewAdmin() || checkUserIsCustomReviewAdmin()),
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.review.detailReports,
              isAccessible: () => checkReviewReportsEnabled(),
            },
            allReports: {
              path: L3_PATHS.team.allReports.review.detailReports,
              isAccessible: () => checkReviewReportsEnabled(),
            },
            view: {
              path: L3_PATHS.team.view.review.detailReports,
              isAccessible: () => checkUserIsReviewAdmin(),
            },
          },
          company: {
            path: L3_PATHS.company.review.detailReports,
            isAccessible: () => checkReviewReportsEnabled(),
          },
        },
        category: 'Analyze',
        hasMetaFilters: true,
      },
      {
        label: `Settings`,
        onClick: () => {
          history.push(L3_PATHS.company.review.settings);
        },
        visible: () =>
          (checkUserIsReviewAdmin() || (checkSimplifiedReviewCreationEnabled() && checkUserIsCustomReviewAdmin())) &&
          l1Module === 'company',
        value: {
          company: {
            path: L3_PATHS.company.review.settings,
            isAccessible: () => false,
          },
        },
        category: 'Configure',
        hasMetaFilters: false,
      },
      {
        id: REVIEW_INTENTS.REVIEW_POST_REVIEW_ACTION_PLAN,
        label: `Growth plan`,
        onClick: () => {
          history.push(L3_PATHS.team.directReports.review.actionPlan);
        },
        visible: () => checkActionPlanFeatureEnabled() && l1Module === 'team' && teamType === 'directReports',
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.review.actionPlan,
              isAccessible: () => true,
            },
          },
        },
        category: 'Action Plan',
        hasMetaFilters: true,
      },
    ];

    return options;
  }, [l1Module, teamType, reviewCalibratedValues, reviewLandingPageIntentChangeHandler, history, userIsManager]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        const isTeamType = l1Module === 'team';

        if (isTalentPlanningPage) {
          if (isTeamType) {
            return !!matchPath(pathname, { path: option.value?.calibrationViewValues?.team.view?.path, exact: true });
          }
          return !!matchPath(pathname, { path: option.value?.calibrationViewValues?.[l1Module]?.path, exact: true });
        }

        if (isDetailedReportPage) {
          if (isTeamType) {
            return !!matchPath(pathname, { path: option.value.team?.[teamType]?.path, exact: true });
          }
          return !!matchPath(pathname, { path: option.value?.company?.path, exact: true });
        }

        // for query param based intent options such as review landing page, pending on me, pending on others etc
        if (option.queryParamValue) {
          return selectedTableId === option.queryParamValue && pathname.includes(option.value.myData.path);
        }
        if (isTeamType) {
          return !!matchPath(pathname, { path: option.value.team?.[teamType]?.path, exact: true });
        }
        return !!matchPath(pathname, { path: option.value[l1Module]?.path, exact: true });
      }),
    [intentDropdownOptions, isDetailedReportPage, isTalentPlanningPage, l1Module, pathname, selectedTableId, teamType]
  );

  useEffect(() => {
    dispatch(setSelectedReviewsIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={
        // @ts-expect-error options type error
        <AgGridEllipsisMenu options={ellipsisMenuOptions} />
      }
      MetaFilters={<MetaFilters />}
      showKebabMenu={isActionPlanPage ? false : showKebabMenu}
      moduleName={meshModules.review.value}
      intentTitle={meshModules.review.label}
      isCategorized
      breadcrumbs={breadcrumbs}
      ellipsisMenuOptions={ellipsisMenuOptions}
    />
  );
};

export { IntentDropdown };
